import React, { useState } from 'react';
import { 
  ArrowLeft, Search, Filter, AlertTriangle, 
  CheckCircle2, Clock, ArrowUpRight
} from 'lucide-react';
import { nonconformanceRecords } from '../../data/qualityControlData';
import { products } from '../../data/inventoryData';

const NonconformancesList = ({ onBack, onViewDetail }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Get product details
  const getProductDetails = (productId) => {
    return products.find(p => p.id === productId) || { name: '未知產品', sku: '' };
  };
  
  // Get severity badge
  const getSeverityBadge = (severity) => {
    switch(severity) {
      case 'minor':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            輕微
          </span>
        );
      case 'major':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            重大
          </span>
        );
      case 'critical':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            嚴重
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            未知
          </span>
        );
    }
  };
  
  // Get status badge
  const getStatusBadge = (status) => {
    switch(status) {
      case 'open':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock className="h-3 w-3 mr-1" />
            待處理
          </span>
        );
      case 'in-progress':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <ArrowUpRight className="h-3 w-3 mr-1" />
            處理中
          </span>
        );
      case 'closed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            已關閉
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            未知
          </span>
        );
    }
  };
  
  // Filter nonconformances
  const filteredNonconformances = nonconformanceRecords.filter(nc => {
    const product = getProductDetails(nc.product_id);
    const matchesSearch = 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      nc.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      nc.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSeverity = filterSeverity === 'all' || nc.severity === filterSeverity;
    const matchesStatus = filterStatus === 'all' || nc.status === filterStatus;
    
    return matchesSearch && matchesSeverity && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button 
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <h1 className="text-2xl font-bold">不合格品記錄</h1>
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            placeholder="搜尋產品或不合格品編號..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex items-center space-x-2 min-w-[200px]">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
          >
            <option value="all">所有嚴重程度</option>
            <option value="minor">輕微</option>
            <option value="major">重大</option>
            <option value="critical">嚴重</option>
          </select>
        </div>
        
        <div className="flex items-center space-x-2 min-w-[200px]">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">所有狀態</option>
            <option value="open">待處理</option>
            <option value="in-progress">處理中</option>
            <option value="closed">已關閉</option>
          </select>
        </div>
      </div>
      
      {/* Nonconformances List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-6 py-3 text-left">不合格品編號</th>
                <th className="px-6 py-3 text-left">產品</th>
                <th className="px-6 py-3 text-left">問題描述</th>
                <th className="px-6 py-3 text-center">數量</th>
                <th className="px-6 py-3 text-center">嚴重程度</th>
                <th className="px-6 py-3 text-left">處理方式</th>
                <th className="px-6 py-3 text-center">狀態</th>
                <th className="px-6 py-3 text-center">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredNonconformances.map(nc => {
                const product = getProductDetails(nc.product_id);
                
                return (
                  <tr key={nc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center">
                        <AlertTriangle className="h-4 w-4 text-amber-500 mr-2" />
                        {nc.id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div className="font-medium text-gray-900">{product.name}</div>
                        <div className="text-xs text-gray-500">{product.sku}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {nc.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                      {nc.quantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {getSeverityBadge(nc.severity)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {nc.disposition === 'return-to-supplier' && '退回供應商'}
                      {nc.disposition === 'rework' && '返工'}
                      {nc.disposition === 'scrap' && '報廢'}
                      {nc.disposition === 'use-as-is' && '特採'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {getStatusBadge(nc.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                      <button 
                        onClick={() => onViewDetail(nc.id)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        查看詳情
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredNonconformances.length === 0 && (
          <div className="py-8 text-center text-gray-500">
            沒有符合條件的不合格品記錄
          </div>
        )}
      </div>
    </div>
  );
};

export default NonconformancesList;
